import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';
import { useAdministrationAPI } from '../AdministrationAPI';
import { useModalContext } from '../components/ModalProvider';
import Section from '../components/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Die Kontaktadresse, die Plattform-Mails des Mandanten statt der von The Real Insight nennen
 * (Fußzeile, Antwort-an). Partner-Mandanten erben die ihres Masters. Gespeichert in
 * `Customer.configuration.notificationEmail`; leer = The Real Insight.
 */
export default function NotificationEmailSection({ className }: { className?: string }) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { getCustomerToolConfiguration, updateCustomerToolConfiguration } = useAdministrationAPI();
    const { pushError, pushSuccess } = useModalContext() as any;
    const id = useId();
    const customer = authentication?.customer;
    const [stored, setStored] = useState<string | null>(null);
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!customer) return;
        let cancelled = false;
        getCustomerToolConfiguration(customer)
            .then((config: any) => {
                if (cancelled) return;
                const email = typeof config?.notificationEmail === 'string' ? config.notificationEmail : '';
                setStored(email);
                setValue(email);
            })
            .catch(() => undefined);
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    if (stored === null) return null;

    const trimmed = value.trim();
    const valid = trimmed === '' || EMAIL.test(trimmed);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await updateCustomerToolConfiguration(customer, { notificationEmail: trimmed });
            const email = typeof updated?.notificationEmail === 'string' ? updated.notificationEmail : trimmed;
            setStored(email);
            setValue(email);
            pushSuccess(t('settingsPanel.userManagementPanel.notificationEmailSaved'));
        } catch (error: any) {
            pushError(error?.message ?? String(error));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Section className={className} title={t('settingsPanel.userManagementPanel.notificationEmail')}>
            <form
                className="flex min-w-0 flex-wrap items-end gap-2"
                onSubmit={(event) => { event.preventDefault(); if (valid && trimmed !== stored) void save(); }}
            >
                <div className="flex min-w-[220px] flex-1 flex-col gap-1.5 sm:max-w-[400px]">
                    <Label htmlFor={id}>{t('settingsPanel.userManagementPanel.notificationEmailLabel')}</Label>
                    <Input
                        id={id}
                        type="email"
                        autoComplete="email"
                        placeholder="info@the-real-insight.ai"
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        aria-invalid={!valid}
                    />
                </div>
                <Button type="submit" disabled={saving || !valid || trimmed === stored} className="max-sm:w-full">
                    {t('global.save')}
                </Button>
            </form>
            <p className="mt-2 text-xs text-muted-foreground">
                {valid ? t('settingsPanel.userManagementPanel.notificationEmailHint') : t('settingsPanel.userManagementPanel.notificationEmailInvalid')}
            </p>
        </Section>
    );
}
